import React, { useEffect, useMemo, useState } from 'react';
import {
  CreditCard,
  Sparkles,
  Bell,
  ShieldCheck,
  Loader2,
  Wallet2,
  Calendar,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  PencilLine,
  ArrowRight,
  Plus,
  CreditCard as CreditCardIcon,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTheme } from '../contexts/ThemeContext';
import { CategorySelector } from './CategorySelector';
import { useFinanceStore, TransactionRecurrence } from '../store/useFinanceStore';
import { toDateInputValue } from '../utils/date';

interface ManagedCard {
  id: string;
  name: string;
  brand: string;
  limit: number;
  closingDay: number;
  dueDay: number;
  bestPurchaseDay: number;
  notificationsEnabled: boolean;
  balanceUsed?: number;
}

const isSupabaseReady = Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY);

type CardInsightsResponse = {
  insights?: string[];
};

type CardPurchase = {
  id: string;
  cardId: string;
  description: string;
  amount: number;
  date: string;
  categoryId: string | null;
  categoryName: string;
  recurrenceType: TransactionRecurrence;
  installments: number | null;
  notes?: string;
};

const CardManager: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const addEvent = useFinanceStore((state) => state.addEvent);
  const categoriesWithSubs = useFinanceStore((state) => state.categoriesWithSubs);
  const [isOpen, setIsOpen] = useState(false);
  const [cards, setCards] = useState<ManagedCard[]>([]);
  const [form, setForm] = useState<Omit<ManagedCard, 'id'>>({
    name: '',
    brand: '',
    limit: 0,
    closingDay: 1,
    dueDay: 5,
    bestPurchaseDay: 2,
    notificationsEnabled: true,
    balanceUsed: 0,
  });
  const [isBestDayAuto, setIsBestDayAuto] = useState(true);
  const [insights, setInsights] = useState<string[]>([]);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState(
    isSupabaseReady
      ? 'Supabase conectado — salve e edite cartões com insights reais.'
      : 'Modo offline seguro — seus cartões ficam guardados aqui até conectar ao Supabase.'
  );
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [transactionModalCard, setTransactionModalCard] = useState<ManagedCard | null>(null);
  const [cardPurchases, setCardPurchases] = useState<Record<string, CardPurchase[]>>(() => {
    try {
      return JSON.parse(localStorage.getItem('card-transactions-cache') || '{}');
    } catch {
      return {};
    }
  });

  const computeBestPurchaseDay = (closingDay: number) => {
    if (!Number.isFinite(closingDay)) return 1;
    const base = Math.min(Math.max(1, Math.floor(closingDay)), 31);
    return base === 31 ? 1 : base + 1;
  };

  useEffect(() => {
    const fetchCards = async () => {
      if (!isSupabaseReady) {
        const localCards = JSON.parse(localStorage.getItem('card-manager-cache') || '[]') as ManagedCard[];
        setCards(localCards);
        setActiveCardIndex(0);
        return;
      }

      try {
        setIsSyncing(true);
        const { data, error } = await supabase.from('cards').select('*').order('created_at', { ascending: false });
        if (error) throw error;

        if (data && data.length) {
          const mapped = data.map((row: any) => ({
            id: row.id,
            name: row.name,
            brand: row.brand,
            limit: Number(row.limit ?? 0),
            closingDay: Number(row.closing_day ?? 1),
            dueDay: Number(row.due_day ?? 5),
            bestPurchaseDay: Number(row.best_purchase_day ?? 2),
            notificationsEnabled: Boolean(row.notifications_enabled),
            balanceUsed: Number(row.balance_used ?? 0),
          })) as ManagedCard[];
          setCards(mapped);
          setActiveCardIndex(0);
          localStorage.setItem('card-manager-cache', JSON.stringify(mapped));
          setStatusMessage('Sincronizado com Supabase — cartões carregados.');
        } else {
          setCards([]);
          setActiveCardIndex(0);
          localStorage.removeItem('card-manager-cache');
          setStatusMessage('Sincronizado com Supabase — nenhum cartão cadastrado ainda.');
        }
      } catch (error) {
        console.warn('Não foi possível carregar cartões do Supabase, usando fallback local.', error);
        const localCards = JSON.parse(localStorage.getItem('card-manager-cache') || '[]') as ManagedCard[];
        setCards(localCards);
        setActiveCardIndex(0);
        setStatusMessage('Offline seguro — conecte ao Supabase quando quiser sincronizar e gerar insights.');
      } finally {
        setIsSyncing(false);
      }
    };

    fetchCards();
  }, []);

  useEffect(() => {
    localStorage.setItem('card-transactions-cache', JSON.stringify(cardPurchases));
  }, [cardPurchases]);

  useEffect(() => {
    if (isBestDayAuto) {
      setForm((prev) => ({ ...prev, bestPurchaseDay: computeBestPurchaseDay(prev.closingDay) }));
    }
  }, [form.closingDay, isBestDayAuto]);

  const utilization = useMemo(() => {
    const totalLimit = cards.reduce((acc, card) => acc + (card.limit || 0), 0);
    const totalUsed = cards.reduce((acc, card) => acc + (card.balanceUsed || 0), 0);
    return {
      totalLimit,
      totalUsed,
      ratio: totalLimit > 0 ? (totalUsed / totalLimit) * 100 : 0,
    };
  }, [cards]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const id = editingId ?? crypto.randomUUID();
    const payload: ManagedCard = {
      ...form,
      id,
      balanceUsed: form.balanceUsed ?? 0,
    };

    setCards((prev) => {
      const updated = editingId ? prev.map((card) => (card.id === editingId ? payload : card)) : [payload, ...prev];
      localStorage.setItem('card-manager-cache', JSON.stringify(updated));
      return updated;
    });
    setForm({
      ...form,
      name: '',
      brand: '',
      limit: 0,
      balanceUsed: 0,
      bestPurchaseDay: computeBestPurchaseDay(form.closingDay),
    });
    setIsBestDayAuto(true);
    setEditingId(null);
    setActiveCardIndex(0);

    if (!isSupabaseReady) return;

    try {
      const { error } = await supabase.from('cards').upsert({
        id: payload.id,
        name: payload.name,
        brand: payload.brand,
        limit: payload.limit,
        closing_day: payload.closingDay,
        due_day: payload.dueDay,
        best_purchase_day: payload.bestPurchaseDay,
        notifications_enabled: payload.notificationsEnabled,
        balance_used: payload.balanceUsed,
      });
      if (error) throw error;
    } catch (error) {
      console.warn('Falha ao salvar cartão no Supabase, mantendo apenas localmente.', error);
    }
  };

  const startEdit = (card: ManagedCard) => {
    setForm({
      name: card.name,
      brand: card.brand,
      limit: card.limit,
      closingDay: card.closingDay,
      dueDay: card.dueDay,
      bestPurchaseDay: card.bestPurchaseDay,
      notificationsEnabled: card.notificationsEnabled,
      balanceUsed: card.balanceUsed ?? 0,
    });
    setEditingId(card.id);
    setIsBestDayAuto(false);
    setIsOpen(true);
  };

  const startCardPurchase = (card: ManagedCard) => {
    setTransactionModalCard(card);
  };

  const handleCardPurchase = async (payload: Omit<CardPurchase, 'id'>) => {
    const purchase: CardPurchase = { ...payload, id: crypto.randomUUID() };

    setCardPurchases((prev) => ({
      ...prev,
      [purchase.cardId]: [purchase, ...(prev[purchase.cardId] || [])],
    }));

    const increment = Math.abs(purchase.amount);
    let nextBalanceUsed = increment;

    setCards((prev) => {
      const updated = prev.map((card) => {
        if (card.id === purchase.cardId) {
          nextBalanceUsed = (card.balanceUsed ?? 0) + increment;
          return { ...card, balanceUsed: nextBalanceUsed };
        }
        return card;
      });
      localStorage.setItem('card-manager-cache', JSON.stringify(updated));
      return updated;
    });

    if (transactionModalCard) {
      const cardName = transactionModalCard.name;
      const installmentsLabel = purchase.installments ? `${purchase.installments}x` : 'à vista';
      const eventTitle = `Compra no cartão ${cardName}`;
      const description = `${purchase.description} (${installmentsLabel})`;

      await addEvent({
        title: eventTitle,
        description,
        type: 'payment',
        amount: Math.abs(purchase.amount),
        date: purchase.date,
        time: '09:00',
        status: 'scheduled',
        transactionType: 'expense',
        metadata: {
          cardId: purchase.cardId,
          categoryId: purchase.categoryId,
          categoryName: purchase.categoryName,
          recurrenceType: purchase.recurrenceType,
          installments: purchase.installments,
        },
      });
    }

    if (isSupabaseReady) {
      try {
        await supabase.from('card_transactions').upsert({
          id: purchase.id,
          card_id: purchase.cardId,
          description: purchase.description,
          amount: purchase.amount,
          date: purchase.date,
          category_id: purchase.categoryId,
          category_name: purchase.categoryName,
          recurrence_type: purchase.recurrenceType,
          installments: purchase.installments,
          notes: purchase.notes ?? null,
        });

        await supabase.from('cards').update({ balance_used: nextBalanceUsed }).eq('id', purchase.cardId);
      } catch (error) {
        console.warn('Falha ao salvar transação de cartão no Supabase.', error);
      }
    }
  };

  const buildLocalInsights = () => {
    if (!cards.length)
      return [
        'Cadastre um cartão com limite, fechamento e vencimento para ver recomendações personalizadas.',
        'Ative alertas para ser avisado sobre o melhor dia de compra e datas críticas da fatura.',
      ];
    const highestUtil = cards.reduce((prev, card) =>
      (card.balanceUsed ?? 0) / (card.limit || 1) > (prev.balanceUsed ?? 0) / (prev.limit || 1) ? card : prev
    );
    return [
      `Melhor dia de compra: use o cartão ${highestUtil.name} até o dia ${highestUtil.bestPurchaseDay} para ganhar até 40 dias de fôlego na fatura.`,
      `Utilização total em ${(utilization.ratio || 0).toFixed(1)}% — mantenha abaixo de 35% para proteger seu score.`,
    ];
  };

  const handleGenerateInsights = async () => {
    if (!cards.length) {
      setInsights(buildLocalInsights());
      return;
    }

    if (!isSupabaseReady) {
      setInsights(buildLocalInsights());
      return;
    }

    setIsLoadingInsights(true);
    try {
      const { data, error } = await supabase.functions.invoke<CardInsightsResponse>('card-insights', {
        body: { cards },
      });
      if (error) throw error;
      setInsights(data?.insights ?? buildLocalInsights());
    } catch (error) {
      console.warn('Falha ao gerar insights via Edge Function, usando heurística local.', error);
      setInsights(buildLocalInsights());
    } finally {
      setIsLoadingInsights(false);
    }
  };

  const goToPrevCard = () => {
    setActiveCardIndex((prev) => (cards.length ? (prev - 1 + cards.length) % cards.length : 0));
  };

  const goToNextCard = () => {
    setActiveCardIndex((prev) => (cards.length ? (prev + 1) % cards.length : 0));
  };

  useEffect(() => {
    setActiveCardIndex((prev) => (cards.length === 0 ? 0 : Math.min(prev, cards.length - 1)));
  }, [cards.length]);

  return (
    <>
      <div
        className={`rounded-2xl p-6 shadow-xl transition-colors space-y-5 ${
          isDark
            ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-slate-800'
            : 'bg-gradient-to-br from-white via-teal-50 to-cyan-50 border border-teal-200 shadow-teal-100/40'
        }`}
      >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className={`text-xs uppercase tracking-[0.18em] ${isDark ? 'text-emerald-200/70' : 'text-emerald-700'}`}>
            Cartões e IA
          </p>
          <h3 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Simplifique seus cartões e receba insights inteligentes
          </h3>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Centralize limites, datas-chave e alertas para destravar dicas automáticas de consumo saudável.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
              isDark ? 'bg-slate-800/70 text-slate-100' : 'bg-white text-slate-700 border border-teal-200'
            }`}
          >
            {isSyncing ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} className="text-emerald-500" />}
            <span>{statusMessage}</span>
          </div>
          <button
            onClick={() => setIsOpen((prev) => !prev)}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
              isDark
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-slate-900'
                : 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white'
            }`}
          >
            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {isOpen ? 'Recolher painel' : 'Abrir painel'}
          </button>
        </div>
      </div>

      <div
        className={`flex flex-wrap items-center gap-4 rounded-xl px-4 py-3 ${
          isDark ? 'bg-slate-900/70 border border-slate-800' : 'bg-white/90 border border-teal-200'
        }`}
      >
        <div className="flex items-center gap-2 text-sm">
          <Wallet2 className={isDark ? 'text-emerald-300' : 'text-emerald-600'} size={18} />
          <div className="flex flex-col">
            <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>Limite total</span>
            <strong className={isDark ? 'text-white' : 'text-slate-900'}>
              {utilization.totalLimit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </strong>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <ArrowRight size={16} className={isDark ? 'text-emerald-300' : 'text-emerald-600'} />
          <div className="flex flex-col">
            <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>Uso atual</span>
            <strong className={isDark ? 'text-white' : 'text-slate-900'}>
              {utilization.totalUsed.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </strong>
          </div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-28 h-2 rounded-full ${isDark ? 'bg-slate-800' : 'bg-teal-100'}`}>
            <div
              className={`h-full rounded-full ${
                utilization.ratio > 80
                  ? isDark
                    ? 'bg-rose-400'
                    : 'bg-rose-500'
                  : isDark
                    ? 'bg-gradient-to-r from-emerald-400 via-cyan-400 to-amber-300'
                    : 'bg-gradient-to-r from-emerald-500 via-cyan-500 to-amber-400'
              }`}
              style={{ width: `${Math.min(100, utilization.ratio)}%` }}
            />
          </div>
          <span className={isDark ? 'text-slate-200 text-xs' : 'text-slate-600 text-xs'}>{utilization.ratio.toFixed(0)}% do limite</span>
        </div>
      </div>

      {isOpen && (
        <div className="space-y-5">
          <div
            className={`rounded-xl p-4 space-y-4 ${
              isDark ? 'bg-slate-900/70 border border-slate-800' : 'bg-white/90 border border-teal-200 shadow-inner shadow-teal-100/20'
            }`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2 space-y-3">
                <div className="flex items-center gap-2">
                  <Info className={isDark ? 'text-emerald-200' : 'text-emerald-600'} size={18} />
                  <div>
                    <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Como usar este painel
                    </p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      Cadastre ou edite cartões para receber dicas sob medida sobre melhor dia de compra, limites e alertas.
                    </p>
                  </div>
                </div>
                <ul className={`list-disc pl-5 space-y-1 text-sm ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                  <li>Preencha nome, bandeira, limite e uso atual para acompanhar a saúde do crédito.</li>
                  <li>Informe fechamento, vencimento e melhor dia sugerido para habilitar alertas.</li>
                  <li>Salve para espelhar com o Supabase e clique em “Gerar insights” para recomendações.</li>
                </ul>
              </div>
              <div className="lg:col-span-3">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <CreditCard className={isDark ? 'text-emerald-300' : 'text-emerald-600'} size={18} />
                    <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {editingId ? 'Editar cartão' : 'Cadastrar cartão'}
                    </h4>
                  </div>
                  {editingId && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(null);
                        setForm({
                          ...form,
                          name: '',
                          brand: '',
                          limit: 0,
                          balanceUsed: 0,
                          bestPurchaseDay: computeBestPurchaseDay(form.closingDay),
                        });
                        setIsBestDayAuto(true);
                      }}
                      className={`text-xs px-2 py-1 rounded-md border ${
                        isDark ? 'border-slate-700 text-slate-200' : 'border-teal-200 text-teal-700'
                      }`}
                    >
                      Cancelar edição
                    </button>
                  )}
                </div>
                <form className="space-y-3" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Nome no app</label>
                      <input
                        required
                        value={form.name}
                        onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                        className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                          isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                        }`}
                        placeholder="Cartão principal"
                      />
                    </div>
                    <div>
                      <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Bandeira</label>
                      <input
                        value={form.brand}
                        onChange={(e) => setForm((prev) => ({ ...prev, brand: e.target.value }))}
                        className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                          isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                        }`}
                        placeholder="Visa, Mastercard..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Limite</label>
                      <input
                        type="number"
                        min={0}
                        value={form.limit}
                        onChange={(e) => setForm((prev) => ({ ...prev, limit: Number(e.target.value) }))}
                        className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                          isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                        }`}
                        placeholder="8000"
                      />
                    </div>
                    <div>
                      <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        Uso atual (opcional)
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={form.balanceUsed}
                        onChange={(e) => setForm((prev) => ({ ...prev, balanceUsed: Number(e.target.value) }))}
                        className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                          isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                        }`}
                        placeholder="3000"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Fechamento</label>
                      <input
                        type="number"
                        min={1}
                        max={31}
                        value={form.closingDay}
                        onChange={(e) => setForm((prev) => ({ ...prev, closingDay: Number(e.target.value) }))}
                        className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                          isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                        }`}
                      />
                    </div>
                    <div>
                      <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Vencimento</label>
                      <input
                        type="number"
                        min={1}
                        max={31}
                        value={form.dueDay}
                        onChange={(e) => setForm((prev) => ({ ...prev, dueDay: Number(e.target.value) }))}
                        className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                          isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                        }`}
                      />
                    </div>
                    <div>
                      <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Melhor dia</label>
                      <input
                        type="number"
                        min={1}
                        max={31}
                        value={form.bestPurchaseDay}
                        onChange={(e) => {
                          setIsBestDayAuto(false);
                          setForm((prev) => ({ ...prev, bestPurchaseDay: Number(e.target.value) }));
                        }}
                        className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                          isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                        }`}
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <input
                        id="notifications"
                        type="checkbox"
                        checked={form.notificationsEnabled}
                        onChange={(e) => setForm((prev) => ({ ...prev, notificationsEnabled: e.target.checked }))}
                      />
                      <label htmlFor="notifications" className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        Receber alertas de fechamento, vencimento e melhor dia de compra
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="submit"
                      className={`inline-flex justify-center items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${
                        isDark
                          ? 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-slate-900'
                          : 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white'
                      }`}
                    >
                      <PlusIcon />
                      {editingId ? 'Salvar alterações' : 'Salvar cartão'}
                    </button>
                    <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      O melhor dia é sugerido automaticamente: dia seguinte ao fechamento.
                    </span>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <CreditCard className={isDark ? 'text-emerald-300' : 'text-emerald-600'} size={18} />
                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Meus cartões</p>
              </div>
              {cards.length > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={goToPrevCard}
                    className={`p-2 rounded-lg ${isDark ? 'bg-slate-800 text-slate-100' : 'bg-white border border-teal-200 text-teal-700'}`}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={goToNextCard}
                    className={`p-2 rounded-lg ${isDark ? 'bg-slate-800 text-slate-100' : 'bg-white border border-teal-200 text-teal-700'}`}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>

            {cards.length === 0 ? (
              <div
                className={`rounded-xl p-4 space-y-2 ${
                  isDark ? 'bg-slate-900/70 border border-slate-800 text-slate-200' : 'bg-white/90 border border-slate-200 text-slate-700'
                }`}
              >
                <p className="font-semibold">Nenhum cartão cadastrado ainda</p>
                <p className="text-sm">Use o formulário acima para registrar cartões, salvar no Supabase e liberar alertas e insights.</p>
              </div>
            ) : (
              <div className={`relative overflow-hidden rounded-2xl border ${isDark ? 'border-slate-800/40 bg-slate-900/60' : 'border-slate-200/70 bg-white/90'} transition-colors`}>
                <div
                  className="flex transition-transform duration-300"
                  style={{ width: `${cards.length * 100}%`, transform: `translateX(-${activeCardIndex * (100 / cards.length)}%)` }}
                >
                  {cards.map((card) => {
                    const usageRatio = card.limit > 0 ? ((card.balanceUsed ?? 0) / card.limit) * 100 : 0;
                    return (
                      <div key={card.id} className="w-full shrink-0 p-4 md:p-6 space-y-4" style={{ width: `${100 / cards.length}%` }}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="space-y-1">
                    <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{card.name}</p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{card.brand || 'Bandeira não informada'}</p>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                              <Calendar size={14} className={isDark ? 'text-emerald-300' : 'text-emerald-600'} />
                              <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>
                                Fecha dia {card.closingDay} · vence dia {card.dueDay}
                              </span>
                      <span className={`text-xs ${isDark ? 'text-emerald-200' : 'text-emerald-600'}`}>
                        Melhor dia: {card.bestPurchaseDay}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => startEdit(card)}
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ${
                        isDark ? 'bg-slate-800 text-slate-100' : 'bg-white border border-teal-200 text-teal-700'
                      }`}
                    >
                      <PencilLine size={14} /> Editar cartão
                    </button>
                    <button
                      type="button"
                      onClick={() => startCardPurchase(card)}
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ${
                        isDark
                          ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-500/40'
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}
                    >
                      <CreditCardIcon size={14} /> Nova compra
                    </button>
                  </div>
                </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Limite</p>
                            <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {card.limit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </p>
                          </div>
                          <div>
                            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Uso</p>
                            <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {(card.balanceUsed ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </p>
                          </div>
                          <div>
                            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Utilização</p>
                            <p className={`font-semibold ${isDark ? 'text-emerald-200' : 'text-emerald-600'}`}>{usageRatio.toFixed(0)}%</p>
                          </div>
                          <div>
                            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Alertas</p>
                            <p className={`font-semibold ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
                              {card.notificationsEnabled ? 'Ativos' : 'Desativados'}
                            </p>
                          </div>
                        </div>

                        <div className={`h-2 w-full rounded-full ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
                          <div
                            className={`h-full rounded-full ${
                              usageRatio > 80
                                ? isDark
                                  ? 'bg-rose-400'
                                  : 'bg-rose-500'
                                : isDark
                                  ? 'bg-emerald-400'
                                  : 'bg-emerald-500'
                            }`}
                            style={{ width: `${Math.min(100, usageRatio)}%` }}
                          />
                        </div>

                        <div className="text-xs flex flex-wrap gap-3 items-center">
                          {card.notificationsEnabled ? (
                            <span className={`inline-flex items-center gap-1 ${isDark ? 'text-emerald-200' : 'text-emerald-700'}`}>
                              <Bell size={12} /> Alertas ativos
                            </span>
                          ) : (
                            <span className={`inline-flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                              <AlertCircle size={12} /> Alertas desativados
                            </span>
                          )}
                          <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                            Dados carregados diretamente do Supabase sempre que possível.
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div
            className={`rounded-xl p-4 space-y-3 ${
              isDark ? 'bg-slate-900/70 border border-slate-800' : 'bg-white/90 border border-slate-200'
            }`}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Sparkles className={isDark ? 'text-amber-300' : 'text-amber-500'} size={18} />
                <div>
                  <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    nossa IA especialista Bia
                  </p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    Gere recomendações personalizadas com a função Edge “card-insights”. Publique com
                    <code className={`ml-1 px-1 rounded ${isDark ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
                      supabase functions deploy card-insights
                    </code>
                    .
                  </p>
                </div>
              </div>
              <button
                onClick={handleGenerateInsights}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${
                  isDark
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-slate-900'
                    : 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white'
                }`}
                disabled={isLoadingInsights}
              >
                {isLoadingInsights ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                Gerar insights
              </button>
            </div>
            <div className="space-y-2 text-sm">
              {(insights.length ? insights : buildLocalInsights()).map((insight, index) => (
                <div
                  key={index}
                  className={`rounded-lg px-3 py-2 ${isDark ? 'bg-slate-800 text-slate-100' : 'bg-teal-50 text-teal-900'}`}
                >
                  {insight}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>
      {transactionModalCard && (
        <CardPurchaseModal
          card={transactionModalCard}
          isDark={isDark}
          onClose={() => setTransactionModalCard(null)}
          onSubmit={(data) => {
            void handleCardPurchase({ ...data, cardId: transactionModalCard.id });
            setTransactionModalCard(null);
          }}
          categoriesAvailable={categoriesWithSubs.length > 0}
        />
      )}
    </>
  );
};

const PlusIcon = () => <span className="font-black text-lg leading-none">+</span>;

interface CardPurchaseModalProps {
  card: ManagedCard;
  isDark: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<CardPurchase, 'id' | 'cardId'> & { cardId?: string }) => void;
  categoriesAvailable: boolean;
}

const CardPurchaseModal: React.FC<CardPurchaseModalProps> = ({ card, isDark, onClose, onSubmit, categoriesAvailable }) => {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(toDateInputValue());
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [installments, setInstallments] = useState('');
  const [recurrenceType, setRecurrenceType] = useState<TransactionRecurrence>('none');
  const [notes, setNotes] = useState('');

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const value = Number.parseFloat(amount.replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0) {
      alert('Informe um valor válido para a compra.');
      return;
    }

    if (!description.trim()) {
      alert('Adicione uma descrição para identificar a compra.');
      return;
    }

    onSubmit({
      description: description.trim(),
      amount: Math.abs(value),
      date,
      categoryId,
      categoryName,
      recurrenceType,
      installments: installments ? Number.parseInt(installments, 10) || null : null,
      notes: notes.trim() || undefined,
    });

    setDescription('');
    setAmount('');
    setCategoryId(null);
    setCategoryName('');
    setInstallments('');
    setRecurrenceType('none');
    setNotes('');
    setDate(toDateInputValue());
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${isDark ? 'bg-black/70' : 'bg-black/50'}`}>
      <div
        className={`max-w-2xl w-full mx-4 rounded-2xl shadow-2xl p-6 space-y-4 ${
          isDark
            ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-900 border border-slate-800'
            : 'bg-white border border-teal-100'
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className={`text-xs uppercase tracking-[0.18em] ${isDark ? 'text-emerald-200/80' : 'text-emerald-700'}`}>
              Nova compra no cartão
            </p>
            <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{card.name}</h3>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Registre despesas do cartão com recorrência ou parcelas e sincronize com a agenda automaticamente.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-lg px-3 py-2 text-sm font-semibold ${
              isDark ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'
            }`}
          >
            Fechar
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Descrição</label>
              <input
                className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                  isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                }`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Compra no mercado"
              />
            </div>
            <div>
              <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Valor</label>
              <input
                type="number"
                min={0}
                className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                  isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                }`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="250.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Data</label>
              <input
                type="date"
                className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                  isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                }`}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div>
              <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Recorrência</label>
              <select
                className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                  isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                }`}
                value={recurrenceType}
                onChange={(e) => setRecurrenceType(e.target.value as TransactionRecurrence)}
              >
                <option value="none">Sem repetição</option>
                <option value="weekly">Semanal</option>
                <option value="biweekly">Quinzenal</option>
                <option value="monthly">Mensal</option>
                <option value="annual">Anual</option>
              </select>
            </div>
            <div>
              <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Parcelas (opcional)</label>
              <input
                type="number"
                min={1}
                className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                  isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
                }`}
                value={installments}
                onChange={(e) => setInstallments(e.target.value)}
                placeholder="Ex.: 6"
              />
            </div>
          </div>

          <div>
            <CategorySelector
              value={categoryId}
              onChange={(id, name) => {
                setCategoryId(id);
                setCategoryName(name);
              }}
              appearance={isDark ? 'dark' : 'light'}
            />
            {!categoriesAvailable && (
              <p className={`mt-2 text-xs ${isDark ? 'text-amber-200/80' : 'text-amber-700'}`}>
                Carregue as categorias para classificar a compra e facilitar os insights.
              </p>
            )}
          </div>

          <div>
            <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Observações (opcional)</label>
            <textarea
              className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
                isDark ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white border border-teal-200'
              }`}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex.: Compra recorrente do streaming"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Alertas e agenda serão criados automaticamente com base na data, recorrência e parcelas.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className={`rounded-lg px-4 py-2 text-sm font-semibold ${
                  isDark ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'
                }`}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${
                  isDark ? 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-slate-900' : 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white'
                }`}
              >
                <Plus size={16} /> Registrar compra
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CardManager;
